import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  /**
   * Handle do requestAnimationFrame pendente.
   * @type {number|null}
   */
  #rafId = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.#scheduleTruncation();
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize);
      this.#calculateTruncation();
    }
  }

  /**
   * Agenda o cálculo para o próximo frame, coalescendo as notificações do
   * ResizeObserver e mantendo a escrita de estilo fora do callback dele.
   */
  #scheduleTruncation() {
    if (this.#rafId != null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#calculateTruncation();
    });
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    // `line-height: normal` não é numérico (parseFloat → NaN). Nesse caso aproxima
    // pelo padrão do browser (~1.2 × font-size), senão o clamp sairia como 'NaN'.
    let lineHeight = parseFloat(computedStyle.lineHeight);
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      lineHeight = parseFloat(computedStyle.fontSize) * 1.2;
    }
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    // Nada mudou: não escreve estilo (a escrita realimentaria o ResizeObserver).
    if (textElement.style.display === '-webkit-box' && textElement.style.webkitLineClamp === String(maxLines)) {
      return;
    }

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle window resize events
   * (campo com arrow function: mantém a mesma referência para o removeEventListener)
   */
  #handleResize = () => {
    this.#scheduleTruncation();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
